import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Search,
  AlertTriangle,
  Calculator,
  Loader2,
  ChevronDown,
  ChevronRight,
  LayoutDashboard,
  BookOpen,
  ShoppingCart,
  ArrowDownLeft,
  ArrowUpRight,
} from 'lucide-react'
import { cardtraderApi, inventoryApi, ordersApi, collectionsApi, api } from '@/services/api'
import { useSidePanelStore } from '@/stores/sidePanelStore'
import type { Card } from '@/types'
import { proxyImageUrl } from '@/utils/imageLoader'

// --- Quick Price Lookup Tool ---
function QuickPriceLookup() {
  const [query, setQuery] = useState('')
  const [selectedCard, setSelectedCard] = useState<Card | null>(null)
  const { openCardModal } = useSidePanelStore()

  const { data: searchResults } = useQuery({
    queryKey: ['toolbag-search', query],
    queryFn: async () => {
      if (query.length < 2) return []
      const { data } = await api.get('/api/cards/search', { params: { q: query } })
      const cards = data.cards || data || []
      return cards.slice(0, 8)
    },
    enabled: query.length >= 2,
    staleTime: 30_000,
  })

  const { data: priceData, isLoading: priceLoading } = useQuery({
    queryKey: ['toolbag-price', selectedCard?.id],
    queryFn: () => cardtraderApi.getCardPrice(selectedCard!.id),
    enabled: !!selectedCard,
    staleTime: 5 * 60_000,
  })

  const { data: breakdown } = useQuery({
    queryKey: ['toolbag-breakdown', selectedCard?.id],
    queryFn: () => collectionsApi.getCardBreakdown(selectedCard!.id),
    enabled: !!selectedCard,
    staleTime: 60_000,
  })

  // cardtraderApi.getCardPrice returns { price: { min_price_cents, avg_price_cents, median_price_cents, ... } | null }
  const price = priceData?.price
  // collectionsApi.getCardBreakdown returns array of { id, name, quantity, foil_quantity, total_quantity }
  const breakdownArr = Array.isArray(breakdown) ? breakdown : []
  const totalStock = breakdownArr.reduce((sum: number, c: any) => sum + (c.total_quantity || 0), 0)

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setSelectedCard(null)
          }}
          placeholder="Search card..."
          className="w-full pl-7 pr-3 py-1.5 text-xs bg-gray-50 dark:bg-dark-bg border border-gray-200 dark:border-dark-border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>

      {/* Search dropdown */}
      {searchResults && searchResults.length > 0 && !selectedCard && (
        <div className="border border-gray-200 dark:border-dark-border rounded overflow-hidden">
          {searchResults.map((card: Card) => (
            <button
              key={card.id}
              onClick={() => {
                setSelectedCard(card)
                setQuery(card.name)
              }}
              className="w-full flex items-center gap-2 px-2 py-1.5 text-left hover:bg-gray-50 dark:hover:bg-dark-border/50 text-xs"
            >
              {card.image_url && (
                <img src={proxyImageUrl(card.image_url)} alt="" className="w-5 h-7 rounded object-cover" />
              )}
              <span className="truncate text-gray-900 dark:text-dark-text">{card.name}</span>
              <span className="text-gray-400 dark:text-dark-muted flex-shrink-0">[{card.set_id}]</span>
            </button>
          ))}
        </div>
      )}

      {/* Price result */}
      {selectedCard && (
        <div className="bg-gray-50 dark:bg-dark-bg rounded p-2 space-y-1.5">
          <div className="flex items-center justify-between">
            <button
              onClick={() => openCardModal(selectedCard)}
              className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline truncate"
            >
              {selectedCard.name}
            </button>
            <span className="text-[10px] text-gray-400">[{selectedCard.set_id}]</span>
          </div>
          {priceLoading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400 mx-auto" />
          ) : price ? (
            <div className="grid grid-cols-3 gap-1 text-[10px]">
              <div className="text-center">
                <div className="text-gray-400">Low</div>
                <div className="font-medium text-green-600">&euro;{(price.min_price_cents / 100).toFixed(2)}</div>
              </div>
              <div className="text-center">
                <div className="text-gray-400">Avg</div>
                <div className="font-medium text-blue-600">&euro;{(price.avg_price_cents / 100).toFixed(2)}</div>
              </div>
              <div className="text-center">
                <div className="text-gray-400">Med</div>
                <div className="font-medium text-purple-600">&euro;{(price.median_price_cents / 100).toFixed(2)}</div>
              </div>
            </div>
          ) : (
            <div className="text-[10px] text-gray-400 text-center">No price data</div>
          )}
          {/* Stock info */}
          {breakdownArr.length > 0 && (
            <div className="text-[10px] text-gray-500 dark:text-dark-muted border-t border-gray-200 dark:border-dark-border pt-1">
              <span className="font-medium">Stock:</span>{' '}
              {totalStock} total
              {breakdownArr.filter((c: any) => c.total_quantity > 0).map((c: any) => (
                <span key={c.id} className="ml-1">
                  ({c.name}: {c.total_quantity})
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// --- Negative Stock Alert ---
function NegativeStockAlert() {
  const navigate = useNavigate()
  const { close } = useSidePanelStore()

  const { data: negativeStock } = useQuery({
    queryKey: ['negative-stock'],
    queryFn: inventoryApi.getNegativeStock,
    staleTime: 5 * 60_000,
  })

  const count = Array.isArray(negativeStock) ? negativeStock.length : 0

  if (count === 0) return null

  return (
    <button
      onClick={() => {
        navigate('/inventory/cards?filter=negative')
        close()
      }}
      className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
    >
      <AlertTriangle className="w-3.5 h-3.5" />
      <span>{count} negative stock item{count !== 1 ? 's' : ''}</span>
    </button>
  )
}

// --- Profit Calculator ---
function ProfitCalculator() {
  const [buyPrice, setBuyPrice] = useState('')
  const [sellPrice, setSellPrice] = useState('')
  const [platform, setPlatform] = useState<'ct' | 'cm'>('ct')

  const buy = parseFloat(buyPrice) || 0
  const sell = parseFloat(sellPrice) || 0
  const feeRate = platform === 'ct' ? 0.06 : 0.05
  const fee = sell * feeRate
  const profit = sell - buy - fee
  const margin = sell > 0 ? ((profit / sell) * 100).toFixed(1) : '0.0'

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <div className="flex-1">
          <label className="text-[10px] text-gray-400 block mb-0.5">Buy &euro;</label>
          <input
            type="number"
            step="0.01"
            value={buyPrice}
            onChange={(e) => setBuyPrice(e.target.value)}
            className="w-full px-2 py-1 text-xs bg-gray-50 dark:bg-dark-bg border border-gray-200 dark:border-dark-border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <div className="flex-1">
          <label className="text-[10px] text-gray-400 block mb-0.5">Sell &euro;</label>
          <input
            type="number"
            step="0.01"
            value={sellPrice}
            onChange={(e) => setSellPrice(e.target.value)}
            className="w-full px-2 py-1 text-xs bg-gray-50 dark:bg-dark-bg border border-gray-200 dark:border-dark-border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>
      <div className="flex gap-1">
        <button
          onClick={() => setPlatform('ct')}
          className={`flex-1 text-[10px] py-0.5 rounded ${platform === 'ct' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 font-medium' : 'bg-gray-50 dark:bg-dark-bg text-gray-500'}`}
        >
          CT (6%)
        </button>
        <button
          onClick={() => setPlatform('cm')}
          className={`flex-1 text-[10px] py-0.5 rounded ${platform === 'cm' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 font-medium' : 'bg-gray-50 dark:bg-dark-bg text-gray-500'}`}
        >
          CM (5%)
        </button>
      </div>
      {(buy > 0 || sell > 0) && (
        <div className="grid grid-cols-3 gap-1 text-[10px] bg-gray-50 dark:bg-dark-bg rounded p-1.5">
          <div className="text-center">
            <div className="text-gray-400">Fee</div>
            <div className="font-medium text-orange-600">&euro;{fee.toFixed(2)}</div>
          </div>
          <div className="text-center">
            <div className="text-gray-400">Profit</div>
            <div className={`font-medium ${profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              &euro;{profit.toFixed(2)}
            </div>
          </div>
          <div className="text-center">
            <div className="text-gray-400">Margin</div>
            <div className={`font-medium ${parseFloat(margin) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {margin}%
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// --- Recent Orders ---
function RecentOrders() {
  const navigate = useNavigate()
  const { close } = useSidePanelStore()

  const { data: orders } = useQuery({
    queryKey: ['toolbag-recent-orders'],
    queryFn: ordersApi.getAll,
    staleTime: 2 * 60_000,
  })

  const recentOrders = Array.isArray(orders) ? orders.slice(0, 5) : []

  if (recentOrders.length === 0) {
    return <div className="text-[10px] text-gray-400 text-center py-2">No recent orders</div>
  }

  const statusColors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    shipped: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    delivered: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  }

  return (
    <div className="space-y-1">
      {recentOrders.map((order: any) => (
        <button
          key={order.id}
          onClick={() => {
            navigate(`/orders/${order.id}`)
            close()
          }}
          className="w-full flex items-center justify-between px-2 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-dark-border/50 rounded transition-colors"
        >
          <div className="flex items-center gap-1.5 min-w-0">
            <ShoppingCart className="w-3 h-3 text-gray-400 flex-shrink-0" />
            <span className="truncate text-gray-700 dark:text-dark-text">
              {order.order_number || order.seller || `#${order.id}`}
            </span>
          </div>
          <span className={`text-[10px] px-1.5 py-0 rounded flex-shrink-0 ${statusColors[order.status] || 'bg-gray-100 text-gray-600'}`}>
            {order.status}
          </span>
        </button>
      ))}
    </div>
  )
}

// --- Lite Ledger ---
function LiteLedger() {
  const navigate = useNavigate()
  const { close, openCardModal } = useSidePanelStore()

  const { data: ledgerData, isLoading } = useQuery({
    queryKey: ['toolbag-ledger'],
    queryFn: () => inventoryApi.getLedger({ limit: 15 }),
    staleTime: 60_000,
  })

  const entries = ledgerData?.entries || []
  const summary = ledgerData?.summary

  const handleCardClick = async (cardId: string) => {
    try {
      const { data: card } = await api.get(`/api/cards/${cardId}`)
      openCardModal(card)
    } catch {
      // ignore
    }
  }

  return (
    <div className="space-y-2">
      {/* Summary bar */}
      {summary && (
        <div className="flex gap-2 text-[10px]">
          <div className="flex-1 bg-green-50 dark:bg-green-900/20 rounded px-2 py-1.5 text-center">
            <div className="text-gray-400 dark:text-dark-muted">In</div>
            <div className="font-medium text-green-600 dark:text-green-400">
              &euro;{(summary.in?.total_value || 0).toFixed(2)}
            </div>
            <div className="text-gray-400">{summary.in?.total_cards || 0} cards</div>
          </div>
          <div className="flex-1 bg-blue-50 dark:bg-blue-900/20 rounded px-2 py-1.5 text-center">
            <div className="text-gray-400 dark:text-dark-muted">Out</div>
            <div className="font-medium text-blue-600 dark:text-blue-400">
              &euro;{(summary.out?.total_value || 0).toFixed(2)}
            </div>
            <div className="text-gray-400">{summary.out?.total_cards || 0} cards</div>
          </div>
        </div>
      )}

      {/* Entries */}
      {isLoading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
        </div>
      ) : entries.length === 0 ? (
        <div className="text-[10px] text-gray-400 text-center py-3">No ledger entries yet</div>
      ) : (
        <div className="space-y-0.5">
          {entries.map((entry: any, i: number) => (
            <button
              key={`${entry.source_table}-${entry.source_id}-${i}`}
              onClick={() => entry.card_id && handleCardClick(entry.card_id)}
              className="w-full flex items-center gap-2 px-2 py-1.5 text-left hover:bg-gray-50 dark:hover:bg-dark-border/50 rounded transition-colors"
            >
              {/* Direction icon */}
              <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                entry.direction === 'in'
                  ? 'bg-green-100 dark:bg-green-900/30'
                  : 'bg-blue-100 dark:bg-blue-900/30'
              }`}>
                {entry.direction === 'in' ? (
                  <ArrowDownLeft className="w-3 h-3 text-green-600 dark:text-green-400" />
                ) : (
                  <ArrowUpRight className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                )}
              </div>

              {/* Card thumbnail */}
              {entry.image_url ? (
                <img src={proxyImageUrl(entry.image_url)} alt="" className="w-5 h-7 rounded object-cover flex-shrink-0" />
              ) : (
                <div className="w-5 h-7 rounded bg-gray-100 dark:bg-dark-border flex-shrink-0" />
              )}

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="text-[11px] text-gray-800 dark:text-dark-text truncate">
                  {entry.card_name || entry.card_id}
                </div>
                <div className="flex items-center gap-1 text-[10px] text-gray-400 dark:text-dark-muted">
                  <span>{entry.quantity + (entry.foil_quantity || 0)}x</span>
                  {entry.price_per_card > 0 && (
                    <span>&euro;{entry.price_per_card.toFixed(2)}</span>
                  )}
                  <span className="opacity-60">{entry.source}</span>
                </div>
              </div>

              {/* Total price */}
              {entry.total_price > 0 && (
                <span className={`text-[10px] font-medium flex-shrink-0 ${
                  entry.direction === 'in' ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400'
                }`}>
                  &euro;{entry.total_price.toFixed(2)}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* View full ledger link */}
      {entries.length > 0 && (
        <button
          onClick={() => {
            navigate('/inventory/ledger')
            close()
          }}
          className="w-full text-[10px] text-blue-600 dark:text-blue-400 hover:underline py-1 text-center"
        >
          View full ledger &rarr;
        </button>
      )}
    </div>
  )
}

// --- Quick Nav Links ---
function QuickNavLinks() {
  const navigate = useNavigate()
  const { close } = useSidePanelStore()

  const links = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
  ]

  return (
    <div className="flex gap-1">
      {links.map(({ icon: Icon, label, path }) => (
        <button
          key={path}
          onClick={() => {
            navigate(path)
            close()
          }}
          className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] text-gray-500 dark:text-dark-muted hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
        >
          <Icon className="w-3.5 h-3.5" />
          {label}
        </button>
      ))}
    </div>
  )
}

// --- Collapsible Section ---
function Section({ title, icon: Icon, children, defaultOpen = true }: {
  title: string
  icon: typeof Search
  children: React.ReactNode
  defaultOpen?: boolean
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div className="border-b border-gray-100 dark:border-dark-border pb-3 last:border-b-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-1.5 text-xs font-medium text-gray-700 dark:text-dark-text mb-2 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
      >
        {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        <Icon className="w-3.5 h-3.5" />
        {title}
      </button>
      {isOpen && children}
    </div>
  )
}

// --- Main Vendor Toolbag Panel ---
export default function VendorToolbagPanel() {
  return (
    <div className="flex flex-col h-full overflow-y-auto p-3 space-y-3">
      {/* Quick Nav */}
      <QuickNavLinks />

      <NegativeStockAlert />

      <Section title="Price Lookup" icon={Search} defaultOpen={true}>
        <QuickPriceLookup />
      </Section>

      <Section title="Ledger" icon={BookOpen} defaultOpen={true}>
        <LiteLedger />
      </Section>

      <Section title="Profit Calculator" icon={Calculator} defaultOpen={false}>
        <ProfitCalculator />
      </Section>

      <Section title="Recent Orders" icon={ShoppingCart} defaultOpen={false}>
        <RecentOrders />
      </Section>
    </div>
  )
}
